import { AdjustmentsHorizontalIcon, ArrowsRightLeftIcon, ClockIcon, LinkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { T } from '../../translation/src';
import { HeroIcon } from '../../types';
import { SuperadminOrganisationFragment, useSuperadminOrganisationByIdQuery } from '../../types/graphqlTypes';
import { ManageReferralsPanel } from './ManageReferralsPanel';
import { PartnerRelationsPanel } from './PartnerRelationsPanel';
import { ReferralHistoryPanel } from './ReferralHistoryPanel';
import { ReferralSettingsPanel } from './ReferralSettingsPanel';

type Tab = 'settings' | 'relations' | 'history' | 'manage';

// The history holds the sales (each with its invoice, the commission or discount on it) and the payouts, for every role.
const TABS: { key: Tab; icon: HeroIcon; label: React.ReactNode }[] = [
  { key: 'settings', icon: AdjustmentsHorizontalIcon, label: <T _str="settings" swc /> },
  { key: 'relations', icon: LinkIcon, label: <T _str="relations" swc /> },
  { key: 'manage', icon: ArrowsRightLeftIcon, label: <T _str="link customers" swc /> },
  { key: 'history', icon: ClockIcon, label: <T _str="history" swc /> },
];

export interface ReferralsModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  organisationName: string;
  referral: SuperadminOrganisationFragment['referral'];
}
// Everything a superadmin does about an organisation's referrals, in one dialog: the tabs on the left, the
// screen on the right — its settings, its own relations as a customer, its history of sales and payouts, and
// who it refers.
const ReferralsModal_: React.FC2<ReferralsModalProps> = ({ onClose, open, organisationId, organisationName, referral }) => {
  const [tab, setTab] = useState<Tab>('settings');
  const active = !!open;
  // Its relations change from elsewhere too (its partner's role, another partner linking it): opened, the
  // organisation is read anew, and the cache brings it to this window and its card.
  useSuperadminOrganisationByIdQuery({ variables: { organisationId }, skip: !active, fetchPolicy: 'network-only' });
  // A fixed height, whatever the tab: switching never moves the dialog; a screen taller than it scrolls inside.
  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="flex flex-col w-full max-w-5xl h-[85vh] max-h-[60rem]">
      <Modal.Header onClose={onClose} className="shrink-0">
        <T _str="partner program" swc /> - {organisationName}
      </Modal.Header>
      <Modal.Body className="flex min-h-0 gap-6 px-1 grow">
        <nav className="flex flex-col w-48 gap-1 pr-4 border-r border-gray-200 shrink-0 dark:border-gray-700">
          {TABS.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={classNames(
                'flex items-center gap-2 px-3 py-2 text-sm font-semibold text-left rounded-md transition',
                tab === key ? 'bg-neon-green-300 text-eerie-black dark:bg-black dark:text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-eerie-black',
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </nav>
        <div className="flex flex-col min-w-0 pr-2 overflow-y-auto grow">
          {tab === 'settings' && <ReferralSettingsPanel organisationId={organisationId} referral={referral} />}
          {tab === 'relations' && <PartnerRelationsPanel referral={referral} />}
          {tab === 'history' && <ReferralHistoryPanel organisationId={organisationId} role={referral.role} active={active} />}
          {tab === 'manage' && <ManageReferralsPanel organisationId={organisationId} organisationName={organisationName} active={active} />}
        </div>
      </Modal.Body>
    </Modal.Container>
  );
};
export const ReferralsModal = memo(ReferralsModal_);
